import { Product } from "src/api/client-api/products/useGetOneProduct";
import { useGetSomeIngredientReferencesUnified } from "src/api/client-api/ingredientReferenceUnified/useGetSomeIngredientReferencesUnified";
import { buildCompositeId } from "src/api/client-api/ingredientReferenceUnified/types";
import { useGetIngredientsPosts } from "src/api/client-api/post/useGetIngredientsPosts";
import { useGetPackagingsPosts } from "src/api/client-api/post/useGetPackagingsPosts";
import { Completions } from "./CompletionService";
import {
    PackagingsCompletionsMetadata,
    ProductPackagingCompletionService,
} from "./Entities/Packagings/PackagingCompletionService";
import {
    ProductIngredientCompletionService,
    IngredientsCompletionsMetadata,
} from "./Entities/Ingredients/IngredientCompletionService";
import { useEntityScoresOrUnavailable } from "src/context/Scores/useEntityScores";
import { useGetProductPosts } from "src/api/client-api/post/useGetProductPosts";
import { ProductCompletionService, ProductCompletionsMetadata } from "./Entities/Product/ProductCompletionService";

type Options = {
    product: Product | undefined;
    campaignId: number | undefined;
    companyId: number | undefined;
};

export type ProductSummary = {
    product: {
        completions: Completions;
        metadata: ProductCompletionsMetadata;
    };
    ingredients: {
        completions: Completions;
        metadata: IngredientsCompletionsMetadata;
    } | null;
    packagings: {
        completions: Completions;
        metadata: PackagingsCompletionsMetadata;
    } | null;
};
export const useProductSummaryData = (options: Options): ProductSummary | null => {
    const { campaignId, companyId, product } = options;

    const ingredientIds = (product?.productIngredients ?? [])
        .filter((pi) => pi.ingredient !== undefined)
        .map((pi) => pi.ingredient!.id);
    const packagingIds = (product?.productPackagings ?? [])
        .filter((pp) => pp.packaging !== undefined && pp.packaging !== null)
        .map((pp) => pp.packaging!.id);

    const { productPosts, isReady: prodPostsReady } = useGetProductPosts(product?.id, companyId, campaignId);
    const { ingredientPosts, isReady: ingPostsReady } = useGetIngredientsPosts(ingredientIds, companyId, campaignId);
    const { packagingPosts, isReady: packPostsReady } = useGetPackagingsPosts(packagingIds);
    const entityScores = useEntityScoresOrUnavailable();

    // Composite ids let us batch-fetch references across both sources from a single endpoint;
    // we default to "sure" for legacy ingredients that pre-date the source column.
    const ingredientRefCompositeIds = product?.productIngredients
        ?.filter((pi) => pi.ingredient !== undefined && pi.ingredient.ingredientReferenceId !== undefined)
        .map((pi) => buildCompositeId(pi.ingredient!.referenceSource ?? "sure", pi.ingredient!.ingredientReferenceId!));
    const { ingredientReferences } = useGetSomeIngredientReferencesUnified(ingredientRefCompositeIds);

    if (!prodPostsReady || !ingPostsReady || !packPostsReady) return null;

    const productCompletions = new ProductCompletionService({
        product,
        productPosts,
    });

    const productIngCompletions = new ProductIngredientCompletionService({
        product,
        ingredientPosts,
        ingredientReferences,
        scoreId: entityScores.isAvailable ? entityScores.lastSelectedVariation.scoreId : undefined,
    });

    const productPackCompletions = new ProductPackagingCompletionService({
        product,
        packagingPosts,
    });

    const summary: ProductSummary = {
        product: {
            completions: productCompletions.getCompletions(),
            metadata: productCompletions.getMetadata(),
        },
        ingredients: {
            completions: productIngCompletions.getCompletions(),
            metadata: productIngCompletions.getMetadata(),
        },
        packagings: {
            completions: productPackCompletions.getCompletions(),
            metadata: productPackCompletions.getMetadata(),
        },
    };

    return summary;
};
